/**
 * Typed dispatch/listen helpers for the Avatar Loop window events.
 *
 * The event NAMES and DETAIL shapes are frozen (`AVATAR_LOOP_CONTRACTS.md`,
 * 27 Aug 2026) — these helpers only apply the frozen types to the standard
 * CustomEvent plumbing so hosts and the SDK stop hand-casting `event.detail`.
 * `avatar:live:contextUpdate` is the pre-existing TM-2 bridge event; the
 * other three are the HostActions lifecycle events introduced by the loop.
 */

import type {
  AvatarLoopWindowEventMap,
  AvatarLoopWindowEventName
} from './contracts';

/** Dispatch one Avatar Loop event with its frozen detail shape. */
export function dispatchAvatarLoopEvent<K extends AvatarLoopWindowEventName>(
  name: K,
  detail: AvatarLoopWindowEventMap[K],
  target: EventTarget = window
): void {
  target.dispatchEvent(new CustomEvent(name, { detail }));
}

/**
 * Listen for one Avatar Loop event. Returns the unsubscribe function (the
 * same convention as `AvatarHostAdapter.subscribe`).
 */
export function addAvatarLoopEventListener<K extends AvatarLoopWindowEventName>(
  name: K,
  listener: (detail: AvatarLoopWindowEventMap[K]) => void,
  target: EventTarget = window
): () => void {
  const handler = (event: Event) => {
    listener((event as CustomEvent<AvatarLoopWindowEventMap[K]>).detail);
  };
  target.addEventListener(name, handler);
  return () => target.removeEventListener(name, handler);
}
